/**
 * Faturas de UM pagamento — o drill-in da tela de Pagamentos.
 *
 * É esta lista que responde ao estado da linha: são as faturas com `paid_by` deste pagamento. Se
 * a soma dos valores recebidos fecha com o pagamento e todas estão pagas, ele está IDENTIFICADO;
 * fechando com faturas abertas, SINALIZADO; qualquer outra coisa, PENDENTE.
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

import BadgeStatus from '../../../atoms/BadgeStatus';
import { PaymentRow, listInvoicesByPayment } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { STATUS_META } from '../PaymentsPanel';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'gold' | 'olive' | 'teal';

const INVOICE_STATUS: Record<string, { label: string; color: BadgeColor }> = {
    OPEN: { label: 'Em aberto', color: 'gold' },
    PAID: { label: 'Paga', color: 'green' },
    CANCELLED: { label: 'Cancelada', color: 'red' },
};

/**
 * O que a linha DIZ quando a fatura está marcada neste pagamento.
 *
 * ⚠️ O status cru mente aqui. Esta é a lista de faturas de UM pagamento, e enquanto ele não for
 * efetivado todas elas estão apenas MARCADAS — dizer "Em aberto" esconde isso, e dizer "Cancelada"
 * é pior ainda: a tela de Identificação marca sempre a fatura ORIGINAL do título, que pode ter sido
 * cancelada numa reemissão, e o usuário fica sem entender onde a marcação dele foi parar.
 *
 * `yellow` é o `bg-yellow-500` — o amarelo de "tem dinheiro apontado, falta aprovar".
 */
const SIGNALLED = { label: 'Pagto Sinalizado', color: 'yellow' as BadgeColor };

interface Props {
    payment: PaymentRow;
    onClose: () => void;
}

const PaymentInvoicesModal: React.FC<Props> = ({ payment, onClose }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setRows(await listInvoicesByPayment(payment.paymentId));
            } catch { setRows([]); }
            setLoading(false);
        })();
    }, [payment.paymentId]);

    const total = Number(payment.amount || 0);
    const sinalizado = rows.reduce((s, inv) => s + Number(inv.amountPaid || 0), 0);
    /**
     * A conta só interessa a quem ainda tem trabalho pela frente. Em IDENTIFICADO ela já fechou
     * por definição — repetir o número ali é ruído.
     */
    const mostraConta = payment.status !== 'IDENTIFIED';
    /**
     * "faltam X" só quando falta ALGO de fato. Com zero marcado, dizer "faltam o valor inteiro" é
     * repetir o que a linha de cima já diz; com a conta fechada, não falta nada.
     */
    const mostraFalta = sinalizado > 0 && Math.abs(sinalizado - total) >= 0.005;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">Faturas do pagamento</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            #{payment.paymentId.slice(0, 8)}
                            {' · '}{STATUS_META[payment.status].label}
                            {' · '}valor {formatBRL(payment.amount || 0)}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    {loading ? (
                        <p className="text-center text-gray-500 py-6">Carregando...</p>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">
                            Nenhuma fatura sinalizada neste pagamento ainda.
                        </p>
                    ) : (
                        <>
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100 dark:bg-slate-700">
                                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                        <th className="px-3 py-2">Fatura</th>
                                        <th className="px-3 py-2">Contrato</th>
                                        <th className="px-3 py-2">Título/NF</th>
                                        <th className="px-3 py-2 text-right">Valor</th>
                                        <th className="px-3 py-2 text-right">Recebido</th>
                                        <th className="px-3 py-2">Vencimento</th>
                                        <th className="px-3 py-2 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((inv: any) => {
                                        // marcada vence o status cru: PAGA nunca chega aqui
                                        // marcada (a efetivação apaga a marcação)
                                        const st = inv.paymentSignalled ? SIGNALLED
                                            : INVOICE_STATUS[inv.status]
                                            || { label: inv.status, color: 'gray' as BadgeColor };
                                        return (
                                            <tr key={inv.invoiceId} className="border-t dark:border-slate-600">
                                                <td className="px-3 py-2 text-xs font-mono text-gray-500" title={inv.invoiceId}>
                                                    {String(inv.invoiceId).slice(0, 8)}
                                                </td>
                                                <td className="px-3 py-2">{inv.contractLabel || '-'}</td>
                                                <td className="px-3 py-2">{inv.details?.documentTitle || '-'}</td>
                                                <td className="px-3 py-2 text-right">{formatBRL(inv.amount)}</td>
                                                <td className="px-3 py-2 text-right">{formatBRL(inv.amountPaid || 0)}</td>
                                                <td className="px-3 py-2">{formatDateBR(inv.dueDate)}</td>
                                                <td className="px-3 py-2 text-center">
                                                    <BadgeStatus text={st.label} color={st.color} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* a conta é o que decide o estado do pagamento — mostrá-la evita que
                                o usuário tenha de somar a coluna com o olho para entender por que
                                a linha ainda está pendente */}
                            {mostraConta && (
                                <div className="mt-4 px-3 py-2 text-sm rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                    Sinalizado: <strong>{formatBRL(sinalizado)}</strong> de{' '}
                                    <strong>{formatBRL(total)}</strong>
                                    {mostraFalta && <> — faltam {formatBRL(total - sinalizado)}.</>}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentInvoicesModal;
